// @givmo/fundraisers-module — the Messages tab (FCM-WEB50; plan §FC-17.5), built to mockup M4 as Jonathan
// ruled it on 2026-10-23 07:07 PDT (D-FCM-64): an organization's message to one of its fundraisers'
// donors, written over the messages it has already delivered, through FCM-BE84's
// `/org/{org_ref}/fundraisers/{fundraiser_id}/donor-messages`.
//
// ⚠ EVERY FACT IS THE SERVER'S. The preview is its own render of the email a named donor gets (the dry
// run's `email_html`), the count is the audience it would fix now, and every refusal is its own sentence,
// shown where the organizer can act on it (decision 4): the fundraiser's state, and a name, in a notice
// above the box; the words' refusals under it, beside the count. Every refusal answers Preview, and the
// words stay in the box. The page decides nothing about who gets a message.
//
// Where it shows is the server's too: the tab exists only while the `/org` detail carries
// `organizer_tools.messages` (the module's OWNER tier, where messages serve; no web knob). Its own
// address without it draws `MessagesUnavailable`.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CircleAlert } from 'lucide-react';
import {
  idempotencyKey,
  isGivmoError,
  sdk,
  type OrganizerMessageHistoryRowOut,
} from '@givmo/api-client';
import {
  orgFundraisersListOrgFundraiserDonorMessagesOptions,
  orgFundraisersListOrgFundraiserDonorMessagesQueryKey,
} from '@givmo/api-client/query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Textarea,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { OrgConsoleUnavailableNotice } from '@givmo/org-console';
import { ConfirmDialog } from './confirm-dialog';
import { useFundraisersModule } from './fundraisers-context';
import { fundraiserOptions } from './fundraisers-queries';
import { FundraisersLoading, FundraisersReadState } from './fundraisers-state';
import { StateNotice } from './lifecycle-ui';
import { MessageEmailFrame } from './message-email-frame';
import { MESSAGE_CAP, MESSAGES_COPY as C } from './messages-copy';
import {
  MESSAGES_PAGE_SIZE,
  MESSAGE_BODY_LIMIT,
  decodePreview,
  fixedRecipientCount,
  historyDay,
  messageLength,
  messageRefusal,
  nextSendMinute,
  nextSendMoment,
  requireMessageRows,
  type MessagePreview,
  type MessageRefusal,
} from './messages-decode';
import { useLivePage } from './use-live-page';

/** One page of the history, under the generated op's own key; the rows are decoded per reader. */
function historyOptions(orgRef: string, fundraiserId: string, offset: number) {
  return {
    ...orgFundraisersListOrgFundraiserDonorMessagesOptions({
      path: { org_ref: orgRef, fundraiser_id: fundraiserId },
      query: { 'page[limit]': MESSAGES_PAGE_SIZE + 1, 'page[offset]': offset },
    }),
    select: requireMessageRows,
    // A route miss or a refusal is an ANSWER (messages are dark here, or not this person's), never a
    // blip: no retry. Anything else keeps the hosts' one retry.
    retry: (failureCount: number, error: unknown) =>
      !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1,
  };
}

/** The tab's own address while no composer serves for this fundraiser here (the Donors tab's pattern). */
export function MessagesUnavailable() {
  return (
    <OrgConsoleUnavailableNotice
      testId="fundraiser-messages-unavailable"
      title={C.tab}
      description={C.unavailable}
    />
  );
}

/** A server sentence with any email address in it as a mailto link (Givmo's pause names support). */
function withMailto(sentence: string) {
  const found = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/.exec(sentence);
  if (found === null) return sentence;
  const address = found[0];
  return (
    <>
      {sentence.slice(0, found.index)}
      <a
        className="font-w600 text-brand-700 underline underline-offset-2"
        href={`mailto:${address}`}
      >
        {address}
      </a>
      {sentence.slice(found.index + address.length)}
    </>
  );
}

/** Size the box to its words. Reset first: a box still holding its last height never measures shorter. */
function fitBox(node: HTMLTextAreaElement) {
  node.style.height = 'auto';
  node.style.height = `${node.scrollHeight + node.offsetHeight - node.clientHeight}px`;
}

/**
 * The box grows with the words, so a long message reads whole (decision 2), never in a scrolled box: on
 * every change of the words, when its width changes and when a web font lands (either wraps the words
 * again: a box fitted under the fallback face stayed a line off once Lato drew). A callback ref, since
 * the box is drawn again each time the preview goes back to it.
 */
function useGrowingBox(text: string) {
  const [node, setNode] = useState<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (node !== null) fitBox(node);
  }, [node, text]);
  useEffect(() => {
    if (node === null) return;
    let width = node.clientWidth;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (node.clientWidth === width) return;
            width = node.clientWidth;
            fitBox(node);
          });
    observer?.observe(node);
    const fonts = typeof document === 'undefined' ? undefined : document.fonts;
    const refit = () => fitBox(node);
    fonts?.addEventListener('loadingdone', refit);
    void fonts?.ready.then(refit);
    return () => {
      observer?.disconnect();
      fonts?.removeEventListener('loadingdone', refit);
    };
  }, [node]);
  return setNode;
}

/** The longest delay a browser keeps a timer for (2^31 − 1 ms, about 24.8 days); a longer wait re-arms. */
const LONGEST_TIMER_MS = 2_147_483_647;

/**
 * Whether the preview's Send still waits (D-FCM-78's decision 7): the dry run named when the next message
 * can go (`next_send_at`), and the minute the line beside Send names hasn't come on this clock. The page
 * wakes when it comes, so Send turns on at the line's own time rather than at the next Preview. The send
 * itself stays the server's call: one pressed early is refused in its own words, with its own date.
 *
 * A timer alone can wake late: its delay may not count the hours a machine sleeps, and a page left open
 * through the wait often spans a night. So the page also looks at the clock whenever it is shown again
 * or takes focus, and Send is on when the organizer comes back at or after the named minute.
 */
function useSendWaits(nextSendAt: string | null): boolean {
  // The `next_send_at` whose minute this clock has seen come.
  const [over, setOver] = useState<string | null>(null);
  useEffect(() => {
    if (nextSendAt === null) return;
    const until = nextSendMinute(nextSendAt);
    let timer: ReturnType<typeof setTimeout> | undefined;
    // One armed timer at a time: a look from an event replaces the timer's, never runs beside it.
    const wake = () => {
      clearTimeout(timer);
      const left = until - Date.now();
      if (left <= 0) setOver(nextSendAt);
      else timer = setTimeout(wake, Math.min(left, LONGEST_TIMER_MS));
    };
    const onShown = () => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onShown);
    window.addEventListener('focus', wake);
    timer = setTimeout(wake, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onShown);
      window.removeEventListener('focus', wake);
    };
  }, [nextSendAt]);
  return nextSendAt !== null && over !== nextSendAt;
}

function Composer({
  orgRef,
  fundraiserId,
  onDelivered,
}: {
  orgRef: string;
  fundraiserId: string;
  onDelivered: () => void;
}) {
  const config = useFundraisersModule();
  const client = useQueryClient();
  const live = useLivePage();
  const [text, setText] = useState('');
  // The dry run's answer and the words it rendered: Send posts exactly those words.
  const [preview, setPreview] = useState<(MessagePreview & { words: string }) | null>(null);
  const [showing, setShowing] = useState(false);
  const [refusal, setRefusal] = useState<MessageRefusal | null>(null);
  const [confirming, setConfirming] = useState(false);
  // One key per message the person means to send, kept while the words are the same: a retry, or a
  // second press, sends it once; changed words are a new message.
  const sendKey = useRef<{ words: string; key: string } | null>(null);
  const box = useGrowingBox(text);
  const length = messageLength(text);
  const path = { org_ref: orgRef, fundraiser_id: fundraiserId };
  // The signature as the server forms it on an organization's fundraiser: the organization's own name.
  // The dry run's is the server's word for it, once one has answered.
  const signedName = preview?.preview.signed_name || config.orgName;

  const readDetailAgain = () =>
    void client.invalidateQueries({ queryKey: fundraiserOptions(orgRef, fundraiserId).queryKey });
  const readHistoryAgain = () =>
    void client.invalidateQueries({
      queryKey: orgFundraisersListOrgFundraiserDonorMessagesQueryKey({ path }),
    });

  /** A refused or failed preview or send: shown where it can be acted on, the words kept. */
  function refused(error: unknown, failed: string, act: 'preview' | 'send') {
    const found = messageRefusal(error);
    // A send that failed may still have gone out (its answer lost on the way), and the weekly limit says
    // one did: the history is read again, so the page shows what the server holds.
    if (act === 'send' || found.place === 'wait') readHistoryAgain();
    setShowing(false);
    if (found.place === 'gone' || found.place === 'failed') {
      // Gone: messages no longer serve here for this person, so the tab follows the server.
      if (found.place === 'gone') readDetailAgain();
      toast.error(failed);
      return;
    }
    // A fundraiser's state moved under the page (closed, deactivated, taken down): the header follows.
    if (found.place === 'notice' || found.place === 'wait') readDetailAgain();
    setRefusal(found);
  }

  const previewAct = useMutation({
    // The words as they stand at the press; the box holds them while the server renders them.
    mutationFn: async (words: string) => {
      const { data } = await sdk.orgFundraisersSendOrgFundraiserDonorMessage({
        path,
        body: { body: words, dry_run: true },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data, words) => {
      if (!live()) return;
      const decoded = decodePreview(data);
      if (decoded === null) {
        toast.error(C.previewFailed);
        return;
      }
      setPreview({ ...decoded, words });
      // A wait says a message went out in the last 3 days, perhaps another organizer's this page hasn't
      // read: the history is read again, so the page shows what the server holds, as a send the wait
      // refuses does.
      if (decoded.nextSendAt !== null) readHistoryAgain();
      setRefusal(null);
      setShowing(true);
    },
    onError: (error) => {
      if (live()) refused(error, C.previewFailed, 'preview');
    },
  });

  const sendAct = useMutation({
    // The words the preview drew, never the box's later ones.
    mutationFn: async (words: string) => {
      if (sendKey.current?.words !== words) sendKey.current = { words, key: crypto.randomUUID() };
      const { data } = await sdk.orgFundraisersSendOrgFundraiserDonorMessage({
        path,
        body: { body: words, dry_run: false },
        ...idempotencyKey(sendKey.current.key),
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      if (!live()) return;
      setConfirming(false);
      toast.success(C.onItsWay(fixedRecipientCount(data) ?? preview?.recipientCount ?? 0));
      sendKey.current = null;
      setText('');
      setPreview(null);
      setShowing(false);
      setRefusal(null);
      onDelivered();
      readHistoryAgain();
    },
    onError: (error) => {
      if (!live()) return;
      setConfirming(false);
      refused(error, C.sendFailed, 'send');
    },
  });

  const wordsRefused = refusal?.place === 'words' ? refusal.message : null;
  const notice = refusal?.place === 'notice' || refusal?.place === 'wait' ? refusal : null;
  // Preview stays on through the wait, which only delays the send (D-FCM-78's decision 7). Any other
  // refusal turns it off while it stands, since pressing it again would only bring the same refusal back:
  // the words' until an edit takes it down, the fundraiser's state or a name until the page reads again.
  const previewHeld = refusal !== null && refusal.place !== 'wait';
  const sendWaits = useSendWaits(preview?.nextSendAt ?? null);
  const note = config.flavor.messagesNote;
  const notesLine = config.flavor.messagesNotesLine;
  return (
    <Card testId="fundraiser-messages-composer">
      <CardHeader title={C.composerTitle} />
      <CardBody className="flex flex-col gap-16">
        <p
          className="text-s14 leading-h15 text-text-secondary"
          {...testIdProp('fundraiser-messages-note')}
        >
          {note}
        </p>
        {notice !== null && (
          // Announced: it answers the person's Preview or Send, as the words' refusal does.
          <StateNotice
            icon={notice.place === 'wait' ? CalendarClock : CircleAlert}
            action={null}
            role="alert"
            testId="fundraiser-message-refusal"
          >
            {withMailto(notice.message)}
            {notice.place === 'wait' && notice.nextSendAt !== null && (
              <> {C.nextSend(nextSendMoment(notice.nextSendAt))}</>
            )}
          </StateNotice>
        )}
        {showing && preview !== null ? (
          <>
            <div className="flex flex-col gap-4">
              <p
                className="text-s16 font-w600 text-text-strong"
                {...testIdProp('fundraiser-message-goes-to')}
              >
                {C.goesTo(preview.recipientCount)}
              </p>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-8 gap-y-2 text-s13">
                <dt className="text-text-secondary">{C.subject}</dt>
                <dd
                  className="text-text wrap-anywhere"
                  {...testIdProp('fundraiser-message-subject')}
                >
                  {preview.preview.subject}
                </dd>
                <dt className="text-text-secondary">{C.fromLabel}</dt>
                <dd className="text-text">{preview.preview.from_name}</dd>
              </dl>
            </div>
            <MessageEmailFrame html={preview.preview.email_html} title={preview.preview.subject} />
            {/* Givmo's two notes, for a host some of whose donors its list doesn't name (the company's);
                a charity's messages reach only donors it names, so its host sets no line. */}
            {notesLine !== undefined && preview.preview.notes.length > 0 && (
              <div className="flex flex-col gap-8" {...testIdProp('fundraiser-message-notes')}>
                <p className="text-s13 text-text-secondary">{notesLine}</p>
                <ul className="flex flex-col gap-6">
                  {preview.preview.notes.map((line) => (
                    <li
                      key={line}
                      className="border-l-2 border-brand-500 bg-surface-sunken py-6 pl-10 pr-8 text-s13 leading-h15 text-text"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-x-16 gap-y-8">
              {/* During the wait the email can be read now, and goes from the named minute (decision 7). */}
              {sendWaits && preview.nextSendAt !== null && (
                <p
                  id="fundraiser-message-next-send"
                  className="text-s13 text-text-secondary"
                  {...testIdProp('fundraiser-message-next-send')}
                >
                  {C.nextSend(nextSendMoment(preview.nextSendAt))}
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-8">
                <Button
                  appearance="saas"
                  tone="secondary"
                  testId="fundraiser-message-edit"
                  disabled={sendAct.isPending}
                  onClick={() => setShowing(false)}
                >
                  {C.edit}
                </Button>
                <Button
                  appearance="saas"
                  tone="primary"
                  testId="fundraiser-message-send"
                  disabled={sendAct.isPending || sendWaits}
                  aria-describedby={sendWaits ? 'fundraiser-message-next-send' : undefined}
                  onClick={() => setConfirming(true)}
                >
                  {C.sendTo(preview.recipientCount)}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p
              className="text-s14 font-w600 text-text-strong wrap-anywhere"
              {...testIdProp('fundraiser-message-from')}
            >
              {C.from(signedName)}
            </p>
            <div className="flex flex-col gap-8">
              <div className="flex items-end justify-between gap-12">
                <label
                  htmlFor="fundraiser-message-body"
                  className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
                >
                  {C.label}
                </label>
                {length === 0 && (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    testId="fundraiser-message-starter"
                    onClick={() => {
                      setText(C.starter);
                      setRefusal((current) => (current?.place === 'words' ? null : current));
                    }}
                  >
                    {C.starterAction}
                  </Button>
                )}
              </div>
              <Textarea
                ref={box}
                id="fundraiser-message-body"
                rows={1}
                maxLength={MESSAGE_BODY_LIMIT}
                // Held while the server renders the preview, so the email it draws is the one that goes.
                readOnly={previewAct.isPending}
                className="min-h-[180px] overflow-hidden"
                value={text}
                error={wordsRefused !== null}
                aria-describedby={
                  wordsRefused !== null
                    ? 'fundraiser-message-error fundraiser-message-count'
                    : 'fundraiser-message-count'
                }
                onChange={(event) => {
                  setText(event.target.value);
                  setRefusal((current) => (current?.place === 'words' ? null : current));
                }}
                testId="fundraiser-message-body"
              />
              {/* The server's refusal of the words on the left, the count on the right, one line. */}
              <div className="flex items-start justify-between gap-12">
                {wordsRefused !== null ? (
                  // Announced: it answers the person's Preview, after the server's round trip.
                  <p
                    id="fundraiser-message-error"
                    role="alert"
                    className="font-sans text-s12 font-w400 tracking-givmo text-danger-600"
                    {...testIdProp('fundraiser-message-error')}
                  >
                    {wordsRefused}
                  </p>
                ) : (
                  <span />
                )}
                <p
                  id="fundraiser-message-count"
                  className={`shrink-0 text-s12 tabular-nums ${length > MESSAGE_CAP ? 'font-w600 text-danger-600' : 'text-text-tertiary'}`}
                  {...testIdProp('fundraiser-message-count')}
                >
                  {C.count(length, MESSAGE_CAP)}
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                appearance="saas"
                tone="primary"
                testId="fundraiser-message-preview"
                loading={previewAct.isPending}
                disabled={length === 0 || previewAct.isPending || previewHeld}
                onClick={() => previewAct.mutate(text)}
              >
                {C.preview}
              </Button>
            </div>
          </>
        )}
      </CardBody>
      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !sendAct.isPending) setConfirming(false);
        }}
        title={C.confirmTitle(preview?.recipientCount ?? 0)}
        body={C.confirmBody}
        confirmLabel={C.confirm}
        testId="fundraiser-message-confirm"
        confirmTestId="fundraiser-message-confirm-send"
        busy={sendAct.isPending}
        onConfirm={() => {
          if (preview !== null) sendAct.mutate(preview.words);
        }}
      />
    </Card>
  );
}

function HistoryRow({ row }: { row: OrganizerMessageHistoryRowOut }) {
  return (
    <li
      className="flex flex-col gap-6 px-20 py-16"
      {...testIdProp(`fundraiser-message-row-${row.id}`)}
    >
      <div className="flex flex-wrap items-center gap-8">
        <p className="text-s13 text-text-secondary">
          {C.delivered(historyDay(row.created_at), row.recipient_count)}
        </p>
        {row.removed_by_givmo && (
          // Red means only a Givmo takedown, as the module's "Taken down" badge (FCM-WEB18 Q6).
          <Badge tone="danger" testId={`fundraiser-message-removed-${row.id}`}>
            {C.removed}
          </Badge>
        )}
      </div>
      {row.signed_name !== '' && (
        <p className="text-s14 font-w600 text-text-strong wrap-anywhere">
          {C.from(row.signed_name)}
        </p>
      )}
      <p
        className={`whitespace-pre-line text-s14 leading-h15 wrap-anywhere ${row.removed_by_givmo ? 'text-text-tertiary' : 'text-text'}`}
      >
        {row.body}
      </p>
    </li>
  );
}

/**
 * The tab: the composer, then the history under it (decision 1). The history's read decides whether the
 * tab still serves: its route miss means messages went dark here after the detail lit the tab, so the
 * detail is read again, once, and the tab follows the server.
 */
export function MessagesTab({ fundraiserId }: { fundraiserId: string }) {
  const config = useFundraisersModule();
  const client = useQueryClient();
  const [offset, setOffset] = useState(0);
  const orgRef = config.orgRef;
  const query = useQuery({
    ...historyOptions(orgRef ?? '', fundraiserId, offset),
    enabled: orgRef !== null,
  });
  const missing = query.isError && isGivmoError(query.error) && query.error.status === 404;
  useEffect(() => {
    if (!missing) return;
    void client.invalidateQueries({
      queryKey: fundraiserOptions(orgRef ?? '', fundraiserId).queryKey,
    });
  }, [missing, client, orgRef, fundraiserId]);

  if (orgRef === null) return <FundraisersLoading />;
  if (missing) return <MessagesUnavailable />;
  if (query.isError && isGivmoError(query.error) && query.error.status === 403)
    return <FundraisersReadState error={query.error} onRetry={() => void query.refetch()} />;

  const rows = query.isSuccess ? query.data.slice(0, MESSAGES_PAGE_SIZE) : [];
  const hasNext = query.isSuccess && query.data.length > MESSAGES_PAGE_SIZE;
  return (
    <div className="flex min-w-0 flex-col gap-20" {...testIdProp('fundraiser-messages')}>
      <Composer orgRef={orgRef} fundraiserId={fundraiserId} onDelivered={() => setOffset(0)} />
      {query.isPending ? (
        <FundraisersLoading />
      ) : (
        <Card testId="fundraiser-messages-history">
          {/* What each count is, once a count is on screen (decision 3). */}
          <CardHeader title={C.historyTitle} subtitle={rows.length > 0 ? C.countLine : undefined} />
          {query.isError ? (
            <EmptyState
              testId="fundraiser-messages-error"
              title={C.failed}
              description={C.failedBody}
              action={
                <Button
                  appearance="saas"
                  tone="secondary"
                  testId="fundraiser-messages-retry"
                  onClick={() => void query.refetch()}
                >
                  {C.retry}
                </Button>
              }
            />
          ) : rows.length === 0 ? (
            // A later page that comes back empty (messages gone between reads) makes no claim; its
            // pager still leads back.
            offset === 0 && <EmptyState testId="fundraiser-messages-empty" title={C.empty} />
          ) : (
            <ul className="divide-y divide-border-subtle">
              {rows.map((row) => (
                <HistoryRow key={row.id} row={row} />
              ))}
            </ul>
          )}
          {!query.isError && (offset > 0 || hasNext) && (
            <div
              className="flex items-center justify-between gap-12 border-t border-border-subtle px-20 py-12"
              {...testIdProp('fundraiser-messages-pagination')}
            >
              <Button
                appearance="saas"
                tone="secondary"
                disabled={offset === 0}
                onClick={() => setOffset((current) => Math.max(0, current - MESSAGES_PAGE_SIZE))}
                testId="fundraiser-messages-previous"
              >
                {C.previous}
              </Button>
              <span className="text-s13 text-text-secondary">
                {C.page(Math.floor(offset / MESSAGES_PAGE_SIZE) + 1)}
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                disabled={!hasNext}
                onClick={() => setOffset((current) => current + MESSAGES_PAGE_SIZE)}
                testId="fundraiser-messages-next"
              >
                {C.next}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
